import { ref } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { useLocalStorage } from '@vueuse/core';
import { apolloProvider } from '@/plugins/apollo';
import { useMoment } from '@/plugins/moment';
import { setGlobalLocale as setGlobalDateFnsLocale } from '@/utils/dateTimeFns';
import { resolveLocale } from '@/utils/locale';
const { locale } = useI18n();
let latestRefetchRequest = 0;
let localeRefetchQueue = Promise.resolve();

function queueLocaleRefetch() {
  const request = ++latestRefetchRequest;
  const queuedRefetch = localeRefetchQueue
    .catch(() => undefined)
    .then(async () => {
      if (request !== latestRefetchRequest) return;
      await apolloProvider.defaultClient.reFetchObservableQueries();
    });
  localeRefetchQueue = queuedRefetch;
  return queuedRefetch;
}

export default function useLocale() {
  const currentLocale = ref(locale);
  const storageLocale = useLocalStorage('userLocale', 'en');
  const { setMomentLocale } = useMoment();
  const applyLocale = async (newLocale, { persist = true, refetch = true } = {}) => {
    const localeChanged = currentLocale.value !== newLocale;
    currentLocale.value = newLocale;
    if (persist) storageLocale.value = newLocale;
    setMomentLocale(newLocale);
    setGlobalDateFnsLocale(newLocale);
    if (localeChanged && refetch) {
      await queueLocaleRefetch();
    }
  };

  const setLocale = async (newLocale, tenantLocales) => {
    const resolvedLocale = resolveLocale(newLocale, tenantLocales);
    await applyLocale(resolvedLocale);
  };

  const restoreLocale = () => {
    void applyLocale('en', { persist: false, refetch: false });
  };

  const validateLocale = async (tenantLocales) => {
    const resolvedLocale = resolveLocale(storageLocale.value, tenantLocales);
    try {
      await applyLocale(resolvedLocale);
    } catch {
      // Tenant settings arrive from a watcher; a transient refetch failure must not be unhandled.
    }
  };

  return {
    locale: currentLocale,
    setLocale,
    restoreLocale,
    validateLocale,
  };
}
